import { getDeliveryDates } from '#common/services/deliveryDateServices'
import { getDeliveryMethods } from '#common/services/deliveryMethodsServices'
import { isValidEmail } from '#common/utils/email'
import { scrollToElement } from '#common/utils/form'
import {
  isPaymentMethodApplePay,
  isPaymentMethodCC,
  isPaymentMethodPayPal,
} from '#common/utils/payment'
import { baseURL } from '#common/utils/url'

export const newCheckoutState = () => {
  return {
    isSignUp: true,
    registration: {
      sectionId: 'registration',
      sectionTitle: 'Your Account',
      enabled: true,
      drawerOpen: true,
      showSectionNumber: true,
      showChangeLink: false,
      registered: false,
      errors: [],
    },
    shipping: {
      sectionId: 'shipping',
      enabled: false,
      drawerOpen: false,
      showChangeLink: false,
      userAddresses: [],
      selectedMethod: null,
      selectedAddress: null,
      selectedPickupAddress: null,
      errors: [],
    },
    deliveryDetails: {
      sectionId: 'delivery-details',
      enabled: false,
      drawerOpen: true,
      showChangeLink: false,
      selectedCarrier: null,
      deliveryMethods: [],
      deliveryDates: null,
      shipDate: null,
      selectedDeliveryDate: null,
      selectedShippingMethod: null,
      previousSelectedHotWeatherOption: null,
      selectedHotWeatherOption: null,
      selectedColdPackOption: null,
      isPreArrival: false,
      showPreArrivalMessage: false,
      holdUntilFall: false,
      errors: [],
    },
    payment: {
      sectionId: 'payment',
      enabled: false,
      drawerOpen: false,
      showChangeLink: false,
      selectedPaymentOption: null,
      selectedPaymentMethod: null,
      tokenType: 0,
      paymentMethods: [],
      oneTimePaymentMethods: [],
      appliedGiftCard: false,
      errors: [],
    },
    giftNote: {
      note: '',
    },
    placeOrder: {
      enabled: false,
      passageOfTitleStatement: '',
      // Following properties are meaningless, but keep it consistent for the section logic
      drawerOpen: false,
      showChangeLink: false,
      errors: [],
    },
  }
}
export const newSMSState = () => {
  return {
    sms_number: '',
    receive_shipping_updates_by_sms: false,
  }
}

export const hotWeatherOptions = [
  {
    id: 'hot-weather-3',
    apiValue: 3,
    name: 'Ship on my selected date',
    description:
      'Deliver my wine on my selected delivery date regardless of temperature.',
  },
  {
    id: 'hot-weather-1',
    apiValue: 1,
    name: 'Ship when the weather is safest',
    description:
      "Notify me if you'd suggest moving my selected delivery date to protect the wine.",
  },
  {
    id: 'hot-weather-4',
    apiValue: 4,
    name: 'Hold until Fall when temperatures subside',
    description:
      'Wine Access will hold your order for no additional charge until weather conditions are ideal for shipping without risk of heat damage. This allows us to fully guarantee your wine.  Any bottles compromised during shipment are eligible to be replaced or credited.',
  },
]

export const coldPackOptions = [
  {
    id: 'use-ice-pack',
    apiValue: true,
    name: '',
  },
  {
    id: 'avoid-ice-pack',
    apiValue: false,
    name: 'Standard packaging',
  },
]

export const registrationCompleted = (userLoaded, loggedIn) => {
  return userLoaded && loggedIn
}

export const shippingCompleted = (checkoutState) => {
  return Boolean(
    checkoutState.shipping.selectedAddress ||
      checkoutState.shipping.selectedPickupAddress,
  )
}

export const deliveryDetailsCompleted = (checkoutState) => {
  return Boolean(
    checkoutState.deliveryDetails.selectedDeliveryDate &&
      checkoutState.deliveryDetails.shipDate &&
      checkoutState.deliveryDetails.selectedShippingMethod,
  )
}

export const paymentCompleted = (checkoutState, cart) => {
  if (cart.grand_total > 0 || cart.is_as_you_go)
    return Boolean(checkoutState.payment.selectedPaymentMethod)
  else return !checkoutState.payment.appliedGiftCard
}

export const hydrateCheckoutState = (checkoutBasket, checkoutState) => {
  try {
    hydrateShippingState(checkoutBasket, checkoutState)
    hydrateDeliveryDetailsState(checkoutBasket, checkoutState)
    hydratePaymentState(checkoutBasket, checkoutState)
    hydrateGiftNote(checkoutBasket, checkoutState)

    checkoutState.placeOrder.passageOfTitleStatement =
      checkoutBasket.passage_of_title_statement
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const hydrateSMSState = (checkoutBasket, smsState) => {
  if (checkoutBasket.user_details !== undefined) {
    smsState.sms_number = checkoutBasket.user_details.sms_number
    smsState.receive_shipping_updates_by_sms =
      checkoutBasket.user_details.receive_shipping_updates_by_sms
  }
}

export const hydrateShippingState = (checkoutBasket, checkoutState) => {
  hydrateCurrentShippingAddress(checkoutBasket, checkoutState)
  if (checkoutBasket.user_addresses !== undefined) {
    checkoutState.shipping.userAddresses = checkoutBasket.user_addresses
  }
}

export const hydrateCurrentShippingAddress = (
  checkoutBasket,
  checkoutState,
) => {
  // No selected address, so keep address null
  if (
    !checkoutBasket.selected_shipping_address &&
    !checkoutBasket.selected_pickup_address
  ) {
    return
  }

  const basketAddress = checkoutBasket.shipping_address
  if (basketAddress) {
    if (basketAddress.id) {
      checkoutState.shipping.selectedAddress = basketAddress
      // If no id, it's a pickup address
    } else {
      checkoutState.shipping.selectedPickupAddress = basketAddress
    }
  } else if (checkoutBasket.user_addresses.length > 0) {
    const idx = checkoutBasket.user_addresses.findIndex(
      (x) => x.is_default_for_shipping === true,
    )
    if (idx !== -1) {
      checkoutState.shipping.selectedAddress =
        checkoutBasket.user_addresses[idx]
    } else {
      checkoutState.shipping.selectedAddress = checkoutBasket.user_addresses[0]
    }
  }
}

export const hydrateDeliveryDetailsState = (checkoutBasket, checkoutState) => {
  checkoutState.deliveryDetails.selectedDeliveryDate =
    checkoutBasket.delivery_date
  checkoutState.deliveryDetails.shipDate = checkoutBasket.ship_date
  checkoutState.deliveryDetails.selectedHotWeatherOption =
    checkoutBasket.hot_weather_option ? checkoutBasket.hot_weather_option : null
  checkoutState.deliveryDetails.selectedColdPackOption =
    checkoutBasket.use_ice_pack
  checkoutState.deliveryDetails.showPreArrivalMessage =
    checkoutBasket.show_pre_arrival_message

  setCarrierAndMethodFromCode(
    checkoutState,
    checkoutBasket.selected_shipping_code,
    checkoutBasket.is_as_you_go,
  )
}

export const hydratePaymentState = (checkoutBasket, checkoutState) => {
  hydratePaymentMethods(checkoutState, checkoutBasket.payment_methods)
  // Only select payment method from basket if there are no one-time payment methods AND
  // there are payment methods on file
  if (
    checkoutState.payment.paymentMethods.length > 0 &&
    checkoutState.payment.oneTimePaymentMethods.length === 0
  ) {
    checkoutState.payment.tokenType = checkoutBasket.payment_token_type || 0
    const method = checkoutState.payment.paymentMethods.find(
      (x) => x.card_id === checkoutBasket.payment_token,
    )
    if (method) {
      checkoutState.payment.selectedPaymentMethod = method.card_id
      if (method.is_paypal) {
        checkoutState.payment.selectedPaymentOption = 'paymentOptionPayPal'
      } else if (method.is_applepay) {
        checkoutState.payment.selectedPaymentOption = 'paymentOptionApplePay'
      } else {
        checkoutState.payment.selectedPaymentOption = 'paymentOptionCreditCard'
      }
    }
  }
}

export const hydratePaymentMethods = (checkoutState, paymentMethods) => {
  const methodsArray = []
  paymentMethods.forEach((paymentMethod) => {
    paymentMethod.is_paypal = isPaymentMethodPayPal(paymentMethod)
    paymentMethod.is_applepay = isPaymentMethodApplePay(paymentMethod)
    paymentMethod.is_cc = isPaymentMethodCC(paymentMethod)
    paymentMethod.one_time = false
    methodsArray.push(paymentMethod)
  })
  checkoutState.payment.paymentMethods = methodsArray
}

export const hydrateGiftNote = (checkoutBasket, checkoutState) => {
  checkoutState.giftNote.note = checkoutBasket.gift_note
}

export const addOneTimePaymentMethod = (checkoutState, data) => {
  if (data.type === 'PayPalAccount') {
    checkoutState.payment.oneTimePaymentMethods.push({
      card_id: data.nonce,
      email: data.details.email,
      first_name: data.details.firstName,
      last_name: data.details.lastName,
      summary_string: `PayPal (${data.details.email})`,
      is_paypal: true,
      is_applepay: false,
      one_time: true,
      is_default: false,
      expiration_date: `${data.details.expirationMonth}-${data.details.expirationYear}`,
    })
    checkoutState.payment.selectedPaymentOption = 'paymentOptionPayPal'
  } else if (data.type === 'ApplePayCard') {
    checkoutState.payment.oneTimePaymentMethods.push({
      card_id: data.nonce,
      first_name: data.billingContact.givenName,
      last_name: data.billingContact.familyName,
      summary_string: `Apple Pay - ${data.paymentMethod.displayName}`,
      is_paypal: false,
      is_applepay: true,
      one_time: true,
      is_default: false,
      expiration_date: `${data.details.expirationMonth}-${data.details.expirationYear}`,
    })
    checkoutState.payment.selectedPaymentOption = 'paymentOptionApplePay'
  } else if (data.type === 'CreditCard') {
    checkoutState.payment.oneTimePaymentMethods.push({
      card_id: data.token,
      summary_string: `${data.details.cardType} •••• •••• •••• ${data.details.lastFour}`,
      is_paypal: false,
      is_applepay: false,
      one_time: true,
      is_default: false,
      expiration_date: `${data.details.expirationMonth}-${data.details.expirationYear}`,
    })
    checkoutState.payment.selectedPaymentOption = 'paymentOptionCreditCard'
  } else {
    throw new Error('Could not determine payment type')
  }
}

export const updateShipping = async ($axios, checkoutState, $store) => {
  let data
  // First check if pickup address is not null, and set that
  if (checkoutState.shipping.selectedPickupAddress) {
    data = await patchBasket(
      $axios,
      {
        selected_pickup_address:
          checkoutState.shipping.selectedPickupAddress.location_id,
        gift_note: '',
        gift_email_address: '',
      },
      $store,
    )
    // Otherwise, check if shipping address is not null, and set that
  } else if (checkoutState.shipping.selectedAddress) {
    try {
      const isGift =
        checkoutState.shipping.methods &&
        checkoutState.shipping.selectedMethod ===
          checkoutState.shipping.methods.addressGift
      data = await patchBasket(
        $axios,
        {
          selected_shipping_address: checkoutState.shipping.selectedAddress.id,
          gift_note: isGift ? checkoutState.giftNote.note : '',
          gift_email_address: isGift ? checkoutState.giftNote.emailAddress : '',
        },
        $store,
      )
    } catch (e) {
      if (
        e.response &&
        e.response.data &&
        e.response.data.selected_shipping_address
      ) {
        checkoutState.shipping.errors =
          e.response.data.selected_shipping_address
      } else {
        throw e
      }
    }
  } else {
    throw new Error(
      'Shipping address nor pickup address is set in checkout state',
    )
  }
  checkoutState.placeOrder.passageOfTitleStatement =
    data.passage_of_title_statement

  await fetchDeliveryMethods(checkoutState, $axios)
  setCarrierAndMethodFromCode(
    checkoutState,
    data.selected_shipping_code,
    data.is_as_you_go,
  )
  await fetchDeliveryDates(checkoutState, $axios)
}

export const updateDeliveryDetailsShippingMethod = async (
  $axios,
  shippingMethod,
  $store,
) => {
  await patchBasket($axios, { selected_shipping_code: shippingMethod }, $store)
}

export const updatePPTOOrder = async ($axios, checkoutState, $store) => {
  return await patchBasket(
    $axios,
    {
      post_purchase_order: checkoutState.postPurchaseOrder,
    },
    $store,
  )
}

export const updateDeliveryDetails = async ($axios, checkoutState, $store) => {
  return await patchBasket(
    $axios,
    {
      selected_shipping_code:
        checkoutState.deliveryDetails.selectedShippingMethod,
      delivery_date: checkoutState.deliveryDetails.selectedDeliveryDate,
      ship_date: checkoutState.deliveryDetails.shipDate,
      hot_weather_option:
        checkoutState.deliveryDetails.selectedHotWeatherOption,
      hold_until_fall: checkoutState.deliveryDetails.holdUntilFall,
      use_ice_pack: checkoutState.deliveryDetails.selectedColdPackOption,
      post_purchase_order: checkoutState.postPurchaseOrder,
    },
    $store,
  )
}

export const updatePackagingChoices = async ($axios, checkoutState, $store) => {
  return await patchBasket(
    $axios,
    {
      use_ice_pack: checkoutState.deliveryDetails.selectedColdPackOption,
    },
    $store,
  )
}

export const updatePaymentMethod = async ($axios, checkoutState, $store) => {
  await patchBasket(
    $axios,
    {
      payment_token: checkoutState.payment.selectedPaymentMethod,
      payment_token_type: checkoutState.payment.tokenType,
    },
    $store,
  )
}

export const updateGiftNote = async ($axios, checkoutState, $store) => {
  await patchBasket(
    $axios,
    {
      gift_note: checkoutState.giftNote.note,
      gift_email_address: checkoutState.giftNote.emailAddress,
    },
    $store,
  )
}

export const resetIcePack = async ($axios, checkoutState, $store) => {
  await patchBasket(
    $axios,
    {
      use_ice_pack: null,
    },
    $store,
  )
}

export const updateLineCart = async ($axios, payload, $store) => {
  const cartId = payload.cart_id
  const lineId = payload.line_id
  delete payload.cart_id
  delete payload.line_id

  // /api/basket/
  // & /api/basket/{cartId}/lines/ share the same backend schema.
  await patchBasket(
    $axios,
    {
      quantity: payload.quantity,
      source_url: payload.source_url,
    },
    $store,
    `/api/basket/${cartId}/lines/${lineId}/`,
  )
}

export const placeOrder = async (
  $axios,
  $route,
  basketId,
  checkoutState,
  smsState,
) => {
  try {
    const data = {
      basket: basketId,
      source_url: baseURL() + $route.fullPath,
    }

    if (smsState) {
      data.receive_shipping_updates_by_sms = smsState.getTextUpdates
      data.sms_number = smsState.mobileNumber
    }
    if (
      checkoutState.shipping.methods &&
      checkoutState.shipping.selectedMethod ===
        checkoutState.shipping.methods.addressGift
    ) {
      if (!checkoutState.giftNote.note) {
        // gift note req'd
        throw new Error('Gift note is required')
      } else if (!checkoutState.giftNote.saved) {
        // gift note changed and not saved
        throw new Error('Gift details have not been saved')
      }
      if (checkoutState.giftNote.emailAddress) {
        if (!isValidEmail(checkoutState.giftNote.emailAddress)) {
          throw new Error('Email address for gift receipient is invalid')
        }
      }
    }
    if (checkoutState.postPurchaseOrder)
      return (await $axios.post('/api/checkout/checkout_ppto/', data)).data
    return (await $axios.post('/api/checkout/checkout/', data)).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const placePPTOOrder = async ($axios, $route, basketId, orderId) => {
  try {
    const data = {
      basket: basketId,
      post_purchase_order: orderId,
      source_url: baseURL() + $route.fullPath,
    }
    return (await $axios.post('/api/checkout/ppto/', data)).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const placeFuturesOrder = async (
  $axios,
  $route,
  basketId,
  extraData,
) => {
  try {
    const data = {
      basket: basketId,
      checkout: 1,
      source_url: baseURL() + $route.fullPath,
      ...extraData,
    }
    return (await $axios.post('/api/checkout/futures/', data)).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getCheckoutBasket = async (
  $axios,
  Sentry,
  resetIcePack = false,
  updateDefaults = false,
) => {
  try {
    const request = await $axios.get('/api/basket/', {
      params: {
        checkout: 1,
        reset_ice_pack: resetIcePack == null ? false : resetIcePack,
        update_defaults: updateDefaults == null ? false : updateDefaults,
      },
    })
    const data = request ? request.data : null
    return data
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    throw e
  }
}

export const getPPTOBasket = async ($axios) => {
  try {
    return (await $axios.get('/api/basket/ppto/', { params: { checkout: 1 } }))
      .data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getFuturesBasket = async ($axios) => {
  try {
    return (
      await $axios.get('/api/basket/futures/', { params: { checkout: 1 } })
    ).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getBasket = async ($axios) => {
  try {
    return (await $axios.get('/api/basket/')).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getCheckoutRecommendations = async ($axios, params = {}) => {
  try {
    return (await $axios.get('/api/catalog/topoff/', { params })).data
      .object_list
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getPPTOCheckoutRecommendations = async ($axios, params = {}) => {
  try {
    return (await $axios.get('/api/catalog/topoff-ppto/', { params })).data
      .object_list
  } catch (e) {
    console.error(e)
    throw e
  }
}

const patchBasket = async ($axios, data, $store, url = '/api/basket/') => {
  try {
    const dataResult = (
      await $axios.patch(url, data, {
        params: { checkout: 1 },
      })
    ).data
    await $store.dispatch('updateCart', dataResult)
    return dataResult
  } catch (e) {
    console.error(e)
    throw e
  }
}

const patchFuturesBasket = async ($axios, data) => {
  try {
    return (
      await $axios.patch('/api/basket/futures/', data, {
        params: { checkout: 1 },
      })
    ).data
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const updateFuturesPaymentMethod = async (
  $axios,
  paymentMethod,
  tokenType,
) => {
  return await patchFuturesBasket($axios, {
    payment_token: paymentMethod,
    payment_token_type: tokenType,
  })
}

export const updateFuturesShippingAddress = async ($axios, addressId) => {
  return await patchFuturesBasket($axios, {
    selected_shipping_address: addressId,
  })
}

export const fetchDeliveryMethods = async (checkoutState, $axios) => {
  try {
    checkoutState.deliveryDetails.deliveryMethods = (
      await getDeliveryMethods($axios)
    ).data.results
    return checkoutState.deliveryDetails.deliveryMethods
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const fetchDeliveryDates = async (checkoutState, $axios, params) => {
  if (!params && checkoutState.postPurchaseOrder) {
    params = { ppto_order: checkoutState.postPurchaseOrder }
  } else if (checkoutState.postPurchaseOrder) {
    params.ppto_order = checkoutState.postPurchaseOrder
  }
  try {
    checkoutState.deliveryDetails.deliveryDates = (
      await getDeliveryDates($axios, params)
    ).data
    // Update the cold pack option
    checkoutState.deliveryDetails.selectedColdPackOption =
      checkoutState.deliveryDetails.deliveryDates.use_ice_pack
    // Update the selected delivery date
    checkoutState.deliveryDetails.selectedDeliveryDate =
      checkoutState.deliveryDetails.deliveryDates.delivery_date
    checkoutState.deliveryDetails.shipDate =
      checkoutState.deliveryDetails.deliveryDates.ship_date
    checkoutState.deliveryDetails.isPreArrival =
      checkoutState.deliveryDetails.deliveryDates.is_pre_arrival
    checkoutState.deliveryDetails.showPreArrivalMessage =
      checkoutState.deliveryDetails.deliveryDates.show_pre_arrival_message
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const fetchDeliveryMethodsAndDates = async (
  checkoutState,
  $axios,
  params,
) => {
  if (!params) params = {}
  const selectedHotWeatherOption = hotWeatherOptions.find(
    (x) =>
      x.apiValue === checkoutState.deliveryDetails.selectedHotWeatherOption,
  )
  if (selectedHotWeatherOption) {
    params.hot_weather_option = selectedHotWeatherOption.apiValue
  }
  if (params.delivery_date === undefined) {
    params.delivery_date = checkoutState.deliveryDetails.selectedDeliveryDate
  }
  const deliveryMethodsPromise = fetchDeliveryMethods(checkoutState, $axios)
  const deliveryDatesPromise = fetchDeliveryDates(checkoutState, $axios, params)

  const [deliveryMethods, deliveryDates] = await Promise.all([
    deliveryMethodsPromise,
    deliveryDatesPromise,
  ])

  return {
    deliveryMethods,
    deliveryDates,
  }
}

const getShippingCarrierFromCode = (shippingCode) => {
  return shippingCode.includes('ups') ? 'UPS' : 'FedEx'
}

const setCarrierAndMethodFromCode = (
  checkoutState,
  shippingCode,
  isAsYouGo,
) => {
  checkoutState.deliveryDetails.selectedCarrier =
    getShippingCarrierFromCode(shippingCode)
  let newShipCode = shippingCode
  if (isAsYouGo) {
    newShipCode = 'as_you_go_' + newShipCode
  }
  checkoutState.deliveryDetails.selectedShippingMethod = newShipCode
}

export const getShippingMethods = (checkoutState) => {
  return checkoutState.deliveryDetails.deliveryMethods
}

export const getSelectedShippingMethod = (checkoutState) => {
  const methods = getShippingMethods(checkoutState)
  return methods.find(
    (x) => x.code === checkoutState.deliveryDetails.selectedShippingMethod,
  )
}

export const selectedShippingMethodToString = (checkoutState) => {
  const selectedShippingMethod = getSelectedShippingMethod(checkoutState)
  if (selectedShippingMethod) {
    let shipName = selectedShippingMethod.name
    if (selectedShippingMethod.code.includes('as_you_go')) {
      shipName = `(${shipName})`
    }
    return `${selectedShippingMethod.carrier} ${shipName}`
  }
  return 'Shipping'
}

export const scrollToSection = (section, $nextTick) => {
  scrollToElement($nextTick, `#${section.sectionId}`)
}

export const handleSectionError = (section, $nextTick) => {
  section.errors = ['"Save & Continue" to proceed']
  scrollToSection(section, $nextTick)
}

export const handleIncomplete = (completed, section, $nextTick) => {
  section.errors = []
  if (!completed) {
    handleSectionError(section, $nextTick)
    return true
  }
  return false
}

export const getCheckoutUrl = (cart, buildEnv) => {
  return '/checkout/'
}

export const getGiftingTaxes = async ($axios, params) => {
  try {
    return (await $axios.get('/api/membership/gift/taxes/', { params })).data
  } catch (e) {
    console.error(e)
    throw e
  }
}
